/**
 * Theme Configuration
 * Defines comprehensive design tokens for multi-domain theming
 * 
 * Theme A (paypaygateway): Pink/Magenta dark premium aesthetic
 * Theme B (runpayrun): Blue/Cyan professional dark theme
 */

export const themes = {
  themeA: {
    name: 'Theme A - PayPayGateway',
    domain: 'paypaygateway',
    
    // Color Palette
    colors: {
      // Primary Colors
      primary: '#d400aa',
      primaryLight: '#ff4bab',
      primaryDark: '#a0007f',
      primaryRgba: 'rgba(255, 75, 171, 0.4)',
      primaryRgbaLight: 'rgba(255, 75, 171, 0.151991)',
      
      // Background
      bgPrimary: '#020a5f',
      bgSecondary: '#0d0d20',
      bgGradientStart: '#020a5f',
      bgGradientEnd: '#0d0d20',
      
      // Glass/Card backgrounds
      cardBg: '#ffffff17',
      cardBgAlt: 'rgba(255, 255, 255, 0.05)',
      
      // Text Colors
      textPrimary: '#ffffff',
      textSecondary: '#ffffff8d',
      textTertiary: 'rgba(255, 255, 255, 0.4)',
      textMuted: '#ffffffb3',
      
      // Border Colors
      borderPrimary: '#737373',
      borderSecondary: '#ffffff8f',
      borderLight: 'rgba(255, 255, 255, 0.2)',
      
      // Status Colors
      success: '#88b04b',
      successLight: '#9abc66',
      successBright: '#7ee787',
      error: '#ff1f1fd8',
      errorLight: '#ff7b7b',
      warning: '#ffb36f',
      warningLight: 'rgb(255, 228, 138)',
      pending: '#ffb36f',
      
      // Interactive Elements
      buttonBg: 'linear-gradient(92deg, #d400aa 0%, #ff4bab 100%)',
      buttonHoverBg: 'linear-gradient(92deg, #a0007f 0%, #d400aa 100%)',
      buttonDisabledBg: '#737373',
      
      // Loader
      loaderColor: '#36d7b7',
      
      // Glow Effects
      glowPrimary: 'rgb(255, 140, 224)',
      glowSecondary: 'rgba(255, 75, 171, 0.3)',
    },
    
    // Typography
    typography: {
      fontFamilyBase: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      fontFamilyHeading: '"Nunito Sans", -apple-system, BlinkMacSystemFont, sans-serif',
      fontFamilyMono: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
      
      fontSizeBase: '16px',
      fontSizeSmall: '14px',
      fontSizeLarge: '18px',
      fontSizeXL: '24px',
      fontSizeXXL: '32px',
      
      fontWeightNormal: '400',
      fontWeightMedium: '500',
      fontWeightSemiBold: '600',
      fontWeightBold: '700',
      
      lineHeightBase: '1.5',
      lineHeightHeading: '1.2',
    },
    
    // Spacing
    spacing: {
      xs: '4px',
      sm: '8px',
      md: '16px',
      lg: '24px',
      xl: '32px',
      xxl: '48px',
    },
    
    // Border Radius
    borderRadius: {
      small: '5px',
      medium: '10px',
      large: '15px',
      round: '50%',
    },
    
    // Shadows
    shadows: {
      card: 'rgb(255 140 224) 0 0 2px 1px',
      cardHover: 'rgb(255 140 224) 0 0 4px 2px',
      button: '0 4px 6px rgba(0, 0, 0, 0.1)',
      input: 'none',
      glow: '0 0 20px rgba(255, 75, 171, 0.3)',
    },
    
    // Gradients
    gradients: {
      background: 'linear-gradient(180deg, #020a5f 0%, #0d0d20 100%)',
      card: 'radial-gradient(144.27% 110.25% at 50.14% -11.38%, rgba(255, 75, 171, 0.4) 0%, rgba(255, 75, 171, 0.151991) 32.9%, rgba(255, 75, 171, 0) 64.5%)',
      button: 'linear-gradient(92deg, #d400aa 0%, #ff4bab 100%)',
      buttonHover: 'linear-gradient(92deg, #a0007f 0%, #d400aa 100%)',
    },
    
    // Effects
    effects: {
      backdropBlur: 'blur(10px)',
      transition: 'all 0.3s ease',
    },
  },
  
  themeB: {
    name: 'Theme B - RunPayRun',
    domain: 'runpayrun',
    
    // Color Palette
    colors: {
      // Primary Colors - Blue/Cyan theme
      primary: '#0066ff',
      primaryLight: '#3d8bff',
      primaryDark: '#0047b3',
      primaryRgba: 'rgba(0, 102, 255, 0.4)',
      primaryRgbaLight: 'rgba(0, 102, 255, 0.15)',
      
      // Background - Darker blue tones
      bgPrimary: '#0a1628',
      bgSecondary: '#0f1419',
      bgGradientStart: '#0a1628',
      bgGradientEnd: '#0f1419',
      
      // Glass/Card backgrounds
      cardBg: '#ffffff12',
      cardBgAlt: 'rgba(255, 255, 255, 0.08)',
      
      // Text Colors
      textPrimary: '#ffffff',
      textSecondary: '#e0e6ed',
      textTertiary: 'rgba(255, 255, 255, 0.5)',
      textMuted: '#a0aec0',
      
      // Border Colors
      borderPrimary: '#2d3748',
      borderSecondary: '#4a5568',
      borderLight: 'rgba(255, 255, 255, 0.15)',
      
      // Status Colors
      success: '#10b981',
      successLight: '#34d399',
      successBright: '#6ee7b7',
      error: '#ef4444',
      errorLight: '#f87171',
      warning: '#f59e0b',
      warningLight: '#fbbf24',
      pending: '#f59e0b',
      
      // Interactive Elements
      buttonBg: 'linear-gradient(92deg, #0066ff 0%, #3d8bff 100%)',
      buttonHoverBg: 'linear-gradient(92deg, #0047b3 0%, #0066ff 100%)',
      buttonDisabledBg: '#4a5568',
      
      // Loader
      loaderColor: '#0066ff',
      
      // Glow Effects
      glowPrimary: 'rgb(61, 139, 255)',
      glowSecondary: 'rgba(0, 102, 255, 0.3)',
    },
    
    // Typography - More modern, tech-focused
    typography: {
      fontFamilyBase: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      fontFamilyHeading: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      fontFamilyMono: '"Fira Code", source-code-pro, Menlo, Monaco, Consolas, monospace',
      
      fontSizeBase: '15px',
      fontSizeSmall: '13px',
      fontSizeLarge: '17px',
      fontSizeXL: '22px',
      fontSizeXXL: '30px',
      
      fontWeightNormal: '400',
      fontWeightMedium: '500',
      fontWeightSemiBold: '600',
      fontWeightBold: '700',
      
      lineHeightBase: '1.6',
      lineHeightHeading: '1.3',
    },
    
    // Spacing - Slightly tighter
    spacing: {
      xs: '4px',
      sm: '8px',
      md: '14px',
      lg: '20px',
      xl: '28px',
      xxl: '40px',
    },
    
    // Border Radius - Sharper edges
    borderRadius: {
      small: '4px',
      medium: '8px',
      large: '12px',
      round: '50%',
    },
    
    // Shadows - Subtler, more refined
    shadows: {
      card: 'rgb(61 139 255) 0 0 2px 1px',
      cardHover: 'rgb(61 139 255) 0 0 4px 2px',
      button: '0 2px 8px rgba(0, 102, 255, 0.2)',
      input: 'none',
      glow: '0 0 20px rgba(0, 102, 255, 0.3)',
    },
    
    // Gradients
    gradients: {
      background: 'linear-gradient(180deg, #0a1628 0%, #0f1419 100%)',
      card: 'radial-gradient(144.27% 110.25% at 50.14% -11.38%, rgba(0, 102, 255, 0.4) 0%, rgba(0, 102, 255, 0.15) 32.9%, rgba(0, 102, 255, 0) 64.5%)',
      button: 'linear-gradient(92deg, #0066ff 0%, #3d8bff 100%)',
      buttonHover: 'linear-gradient(92deg, #0047b3 0%, #0066ff 100%)',
    },
    
    // Effects
    effects: {
      backdropBlur: 'blur(12px)',
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
};

/**
 * Convert theme object to CSS variables
 * @param {Object} theme - Theme configuration object
 * @returns {Object} CSS variables object
 */
export const themeToCSSVariables = (theme) => {
  const cssVars = {};
  
  // Colors
  Object.entries(theme.colors).forEach(([key, value]) => {
    const varName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    cssVars[varName] = value;
  });
  
  // Typography
  Object.entries(theme.typography).forEach(([key, value]) => {
    const varName = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    cssVars[varName] = value;
  });
  
  // Spacing
  Object.entries(theme.spacing).forEach(([key, value]) => {
    cssVars[`--spacing-${key}`] = value;
  });
  
  // Border Radius
  Object.entries(theme.borderRadius).forEach(([key, value]) => {
    cssVars[`--border-radius-${key}`] = value;
  });
  
  // Shadows
  Object.entries(theme.shadows).forEach(([key, value]) => {
    cssVars[`--shadow-${key}`] = value;
  });
  
  // Gradients
  Object.entries(theme.gradients).forEach(([key, value]) => {
    cssVars[`--gradient-${key}`] = value;
  });
  
  // Effects
  Object.entries(theme.effects).forEach(([key, value]) => {
    const varName = `--effect-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    cssVars[varName] = value;
  });
  
  return cssVars;
};

export default themes;
